import {
  AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, ViewChild, ViewEncapsulation, inject,
} from '@angular/core';
import { Router } from '@angular/router';
import { createFormlosFrameDocument, loadFormlosPage } from './formlos-frame';
import { formlosOrigin } from './formlos-origin';

interface HeaderGeometry {
  source: 'formlos-embed';
  kind: 'header';
  type: 'geometry';
  height: number;
  menuBottom: number;
  menuLeft: number | null;
  menuRight: number | null;
  mobileOpen: boolean;
}

const INITIAL_HEIGHT = 165;
const MIN_HEADER_HEIGHT = 40;
const MAX_HEADER_HEIGHT = 1000;
const MAX_FRAME_HEIGHT = 2000;

/** Runs Formlos's own page shell in a clipped frame over the public app. */
@Component({
  encapsulation: ViewEncapsulation.Emulated,
  selector: 'formlos-header',
  standalone: false,
  styles: `
    :host {
      --space-s: clamp(1.125rem, 1.0815rem + 0.2174vw, 1.25rem);
      --space-l: clamp(2.25rem, 2.163rem + 0.4348vw, 2.5rem);

      --fl-h-margin-horizontal: 0;
      --fl-h-margin-vertical: var(--space-s);

      display: block;
      position: relative;
      z-index: 20;
      width: 100%;
    }
    .frame-holder {
      position: relative;
      background: white;
      display: flex;
      justify-content: flex-start;
      align-items: flex-start;
    }

    .frame-holder.loading {
      box-shadow: 0 0 15px #ccc;
    }

    iframe { position: absolute; top: 0; left: 0; width: 100%; border: 0; visibility: hidden; }
    iframe.ready { visibility: visible; }
    .fallback { width: 100%; max-width: var(--split-pane-max-width); margin: 0 auto; }
    .fallback__link { display: block; padding: var(--fl-h-margin-vertical) var(--fl-h-margin-horizontal); }
    .fallback__link img { display: block; width: 180px; max-width: 100%; height: auto; }
    @media (max-width: 1380px) { .fallback__link { --fl-h-margin-horizontal: var(--space-l); }}
  `,
  template: `
    <div class="frame-holder" [style.height.px]="headerHeight" [class.loading]="!loaded" [style.min-height.px]="loaded ? null : initialHeight">
      <iframe #frame title="NAKOS Navigation" [class.ready]="loaded"
        [style.height.px]="frameHeight" [style.clip-path]="clipPath" scrolling="no"></iframe>
      @if (!loaded) {
        <div class="fallback">
          <a class="fallback__link" routerLink="/home" aria-label="NAKOS Startseite">
            <img src="./assets/formlos/logo.svg" alt="NAKOS" />
          </a>
        </div>
      }
    </div>
  `,
})
export class FormlosHeaderComponent implements AfterViewInit, OnDestroy {
  @ViewChild('frame', {
    static: true,
  }) private frame!: ElementRef<HTMLIFrameElement>;

  public readonly initialHeight = INITIAL_HEIGHT;
  public headerHeight = INITIAL_HEIGHT;
  public frameHeight = INITIAL_HEIGHT;
  public clipPath = 'none';
  public loaded = false;

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly router = inject(Router);
  private readonly listeners = new AbortController();
  private destroyed = false;
  private lastGeometry: HeaderGeometry | undefined;

  public ngAfterViewInit(): void {
    window.addEventListener('message', (event) => { this.onMessage(event); }, {
      signal: this.listeners.signal,
    });
    window.addEventListener('resize', () => { this.onResize(); }, {
      signal: this.listeners.signal,
    });
    this.load();
  }

  public ngOnDestroy(): void {
    this.destroyed = true;
    this.listeners.abort();
    this.frame.nativeElement.srcdoc = '';
  }

  private onMessage(event: MessageEvent): void {
    if (event.source !== this.frame.nativeElement.contentWindow) { return; }
    const message = event.data;

    if (!message || message.source !== 'formlos-embed' || message.kind !== 'header') { return; }
    if (message.type === 'geometry') {
      this.applyGeometry(message as HeaderGeometry);
    } else if (message.type === 'app-navigate' && typeof message.path === 'string'
      && message.path.startsWith('/') && !message.path.startsWith('//')) {
      this.router.navigateByUrl(message.path);
    } else if (message.type === 'skip') {
      const page = document.querySelector('.public-scroll-region > ion-router-outlet > .ion-page:not(.ion-page-hidden)');
      const main = page?.querySelector('#home-main, #main, ion-content');

      if (main instanceof HTMLElement) {
        main.setAttribute('tabindex', '-1');
        main.focus();
      }
    }
  }

  private onResize(): void {
    if (this.lastGeometry) { this.applyGeometry(this.lastGeometry); }
  }

  private async load(): Promise<void> {
    try {
      const origin = formlosOrigin();
      const fragments = await loadFormlosPage(origin);

      if (this.destroyed) { return; }
      this.frame.nativeElement.srcdoc = createFormlosFrameDocument(fragments, 'header', origin);
    } catch (error) {
      if (!this.destroyed) { console.error('Could not load Formlos header frame', error); }
    }
  }

  private applyGeometry(message: HeaderGeometry): void {
    if (!Number.isFinite(message.height) || message.height < MIN_HEADER_HEIGHT || message.height > MAX_HEADER_HEIGHT) { return; }
    this.lastGeometry = message;
    this.headerHeight = Math.ceil(message.height);
    const shadow = 16;

    if (message.mobileOpen) {
      this.frameHeight = Math.max(this.headerHeight, Math.ceil(window.innerHeight - this.host.nativeElement.getBoundingClientRect().top));
      this.clipPath = 'none';
    } else if (Number.isFinite(message.menuBottom) && message.menuBottom > this.headerHeight + 2
      && message.menuLeft !== null && message.menuRight !== null) {
      const { width } = this.host.nativeElement.getBoundingClientRect();
      const left = Math.max(0, Math.floor(message.menuLeft) - shadow);
      const right = Math.min(width, Math.ceil(message.menuRight) + shadow);
      const band = this.headerHeight + shadow;

      this.frameHeight = Math.min(MAX_FRAME_HEIGHT, Math.ceil(message.menuBottom) + shadow);
      this.clipPath = `polygon(0 0, 100% 0, 100% ${band}px, ${right}px ${band}px, ${right}px 100%, ${left}px 100%, ${left}px ${band}px, 0 ${band}px)`;
    } else {
      this.frameHeight = this.headerHeight + shadow;
      this.clipPath = 'none';
    }

    this.loaded = true;
    this.changeDetector.detectChanges();
  }
}
